/**
 * Band designs W3 (contract v67) — the host half of the band BODY: the
 * parametric cross-section (Profile tab), the partial step and the true-V
 * groove angle (Joints tab).
 *
 * Pure, so `scripts/check-ring-body.ts` drives every limit and patch without a
 * browser. Every range here is the engine's own clamp (`clampParamProfile`,
 * `partialStepGrooves`, `clampDesignGrooves`) read from the contract, so a
 * slider can never write a value the engine snaps back under the finger.
 *
 * No new RPC name: the section rides `applyRingDesign({ profileId, paramProfile })`
 * and the step / V angle ride `setDesignGrooves`, so `caps.ringBodyV67` (an
 * alias of `ringDesignV67`) is the only gate.
 */
import {
  PARAM_PROFILE_DEFAULTS,
  PARAM_PROFILE_ID,
  PARTIAL_STEP_DEFAULTS,
  clampParamProfile,
  partialStepGrooves,
} from "../../../shared/api-contract";
import type {
  DesignGroove,
  ParamProfileStep,
  PartialStepParams,
  RingDesignPatch,
  ResolvedParamProfile,
  RingState,
} from "../../../shared/api-contract";

// ─── The parametric section ──────────────────────────────────────────────────

export const PARAM_ID = PARAM_PROFILE_ID;

export const isParamProfile = (s: Pick<RingState, "profileId">) => s.profileId === PARAM_PROFILE_ID;

/** What the engine builds for this band: the stored sliders against the defaults, clamped to the band. */
export function resolvedParam(s: Pick<RingState, "paramProfile" | "widthMM" | "thicknessMM">): ResolvedParamProfile {
  return clampParamProfile(s.paramProfile, s.widthMM, s.thicknessMM);
}

export type ParamKey = "crownMM" | "flatTop" | "edgeRadiusMM" | "bevelMM" | "comfortMM";

export interface ParamSliderSpec {
  key: ParamKey;
  label: string;
  min: number;
  max: number;
  step: number;
  display: (v: number) => string;
  hint: string;
}

const mm = (v: number) => `${v.toFixed(2)} mm`;
const round = (v: number, step: number) => Number((Math.round(v / step) * step).toFixed(6));

/**
 * The five section sliders for a band of this width and thickness. The maxima
 * are `clampParamProfile`'s: crown 0.6 T, comfort 0.3 T, bevel 0.4 W (and the
 * wall), edge radius 3 mm (its own wall then limits it, which is what "fully
 * round" means). A slider past a coupled limit (crown + comfort > 0.8 T) is
 * shown at what the engine actually built — the `value` comes from
 * `resolvedParam`, never from the stored number.
 */
export function paramSliders(widthMM: number, thicknessMM: number): ParamSliderSpec[] {
  const W = Math.max(0.5, widthMM);
  const T = Math.max(0.3, thicknessMM);
  return [
    { key: "crownMM", label: "Crown", min: 0, max: round(0.6 * T, 0.01), step: 0.01, display: (v) => (v <= 0 ? "Flat" : mm(v)),
      hint: "How far the middle of the face rises above its edges." },
    { key: "flatTop", label: "Flat Top", min: 0, max: 1, step: 0.05, display: (v) => (v <= 0 ? "Dome" : v >= 1 ? "Flat" : `${Math.round(v * 100)}%`),
      hint: "Width of the flat on top of the crown, as a share of the band's width." },
    // The contract holds a rim to 45 % of the width (beyond it the two bore
    // corners met and folded the section), so the handle stops there.
    { key: "edgeRadiusMM", label: "Edge Radius", min: 0, max: Math.min(3, Math.floor(0.45 * W * 100) / 100), step: 0.05, display: (v) => (v <= 0 ? "Crisp" : mm(v)),
      hint: "0 is a truly crisp corner; large values round the whole side." },
    { key: "bevelMM", label: "Bevel", min: 0, max: round(Math.min(0.4 * W, 0.8 * T), 0.01), step: 0.01, display: (v) => (v <= 0 ? "None" : mm(v)),
      hint: "A 45° chamfer on each outer edge." },
    { key: "comfortMM", label: "Comfort", min: 0, max: round(0.3 * T, 0.01), step: 0.01, display: (v) => (v <= 0 ? "Flat inside" : mm(v)),
      hint: "How far the inside bulges toward the finger." },
  ];
}

/** Choose the parametric section (its sliders keep whatever the ring stored). */
export const selectParamPatch = (): RingDesignPatch => ({ profileId: PARAM_PROFILE_ID });

/** One section slider. The engine MERGES a non-restore `paramProfile`, so only the one field travels. */
export const paramPatch = (key: ParamKey, v: number): RingDesignPatch => ({ paramProfile: { [key]: v } });

/** Back to the everyday dome: every slider at its default, the step removed. */
export const paramResetPatch = (): RingDesignPatch =>
  ({ paramProfile: { ...PARAM_PROFILE_DEFAULTS, step: null } }) as unknown as RingDesignPatch;

export type StepKey = "positionMM" | "heightMM" | "wallDeg";

export interface StepSliderSpec {
  key: StepKey;
  label: string;
  min: number;
  max: number;
  step: number;
  unit?: string;
}

/** The step's three sliders, at `clampParamProfile`'s limits for this band. */
export function stepSliders(widthMM: number, res: ResolvedParamProfile, thicknessMM: number): StepSliderSpec[] {
  const lim = Math.max(0, widthMM / 2 - 0.3);
  const wall = thicknessMM - res.crownMM - res.comfortMM;
  const hMax = Math.max(0.05, Math.min(0.6, wall - res.bevelMM - 0.25 * thicknessMM));
  return [
    { key: "positionMM", label: "Step Position", min: -round(lim, 0.05), max: round(lim, 0.05), step: 0.05, unit: "mm" },
    { key: "heightMM", label: "Step Height", min: 0.05, max: Math.max(0.05, Math.floor(hMax * 100) / 100), step: 0.01, unit: "mm" },
    { key: "wallDeg", label: "Step Wall", min: 20, max: 90, step: 1, unit: "°" },
  ];
}

/** The step the sliders show — the RESOLVED one, so a clamped value reads as built. */
export function currentStep(res: ResolvedParamProfile): ParamProfileStep | null {
  if (!res.step) return null;
  return { side: res.step.side > 0 ? "+y" : "-y", positionMM: res.step.positionMM, heightMM: res.step.heightMM, wallDeg: res.step.wallDeg };
}

/** Switch the one-sided step on: lowered toward +Y from a line a little off centre. */
export const stepOnPatch = (widthMM: number): RingDesignPatch => ({
  paramProfile: { step: { side: "+y", positionMM: round(Math.min(0.15 * widthMM, Math.max(0, widthMM / 2 - 0.3)), 0.05), heightMM: 0.2, wallDeg: 90 } },
});

/** Remove the step (`step: null` is the engine's "delete"; the patch type has no null, hence the cast). */
export const stepOffPatch = (): RingDesignPatch => ({ paramProfile: { step: null } }) as unknown as RingDesignPatch;

/** One step field — the whole step travels (the engine keeps unnamed fields, but a full block is exact). */
export function stepPatch(cur: ParamProfileStep, patch: Partial<ParamProfileStep>): RingDesignPatch {
  return { paramProfile: { step: { ...cur, ...patch } } };
}

/**
 * The section drawn for the summary card, in the `ProfileSection` box
 * (x 8…52, y 6…34; the card stretches it to the band's own aspect). Bore at
 * the bottom with its comfort bulge, crown / flat top / bevel / step on top,
 * the four rim corners rounded by the edge radius. An icon, not the lathe
 * curve — the engine owns the real one.
 */
export function paramSectionPath(res: ResolvedParamProfile, widthMM: number, thicknessMM: number): string {
  const W = Math.max(0.5, widthMM);
  const T = Math.max(0.3, thicknessMM);
  const h = W / 2;
  const X = (x: number) => 8 + ((x + h) / W) * 44;
  const Y = (y: number) => 34 - (y / T) * 28;
  const top = (x: number) => {
    const a = Math.abs(x);
    const f = res.flatTop * h;
    let y = a <= f || h - f <= 1e-9 ? T : T - res.crownMM * ((a - f) / (h - f)) ** 2;
    if (res.step && (x - res.step.positionMM) * res.step.side > 0) y -= res.step.heightMM;
    return y;
  };
  const bore = (x: number) => res.comfortMM * (1 - (1 - (x / h) ** 2));
  const N = 24;
  const pts: [number, number][] = [];
  // Bore, left to right.
  for (let i = 0; i <= N; i++) { const x = -h + (2 * h * i) / N; pts.push([x, bore(x)]); }
  const b = Math.min(res.bevelMM, 0.45 * W);
  const rightTop = top(h - b);
  // Right wall, bevel, top right to left, bevel, left wall.
  pts.push([h, rightTop - b]);
  for (let i = 0; i <= N; i++) { const x = h - b - ((2 * (h - b)) * i) / N; pts.push([x, top(x)]); }
  pts.push([-h, top(-(h - b)) - b]);
  // Round the four rim corners (bore ends, wall tops) by the edge radius, in screen units.
  const rPx = Math.min(10, (res.edgeRadiusMM / W) * 44);
  const corners = new Set([0, N, N + 1, pts.length - 1]);
  const S = pts.map(([x, y]) => [X(x), Y(y)] as [number, number]);
  const f = (n: number) => n.toFixed(2);
  const out: string[] = [];
  const n = S.length;
  for (let i = 0; i < n; i++) {
    const p = S[i];
    if (!corners.has(i) || rPx <= 0.05) { out.push(`${out.length ? "L" : "M"}${f(p[0])} ${f(p[1])}`); continue; }
    const a = S[(i - 1 + n) % n];
    const c = S[(i + 1) % n];
    const toward = (q: [number, number]) => {
      const dx = q[0] - p[0], dy = q[1] - p[1];
      const len = Math.hypot(dx, dy) || 1;
      const k = Math.min(rPx, len / 2) / len;
      return [p[0] + dx * k, p[1] + dy * k];
    };
    const p0 = toward(a);
    const p1 = toward(c);
    out.push(`${out.length ? "L" : "M"}${f(p0[0])} ${f(p0[1])}`, `Q${f(p[0])} ${f(p[1])} ${f(p1[0])} ${f(p1[1])}`);
  }
  return `${out.join(" ")} Z`;
}

// ─── The partial step (Joints tab) ───────────────────────────────────────────

export interface PartialStepGroup {
  group: string;
  params: PartialStepParams;
  ids: string[];
}

const isPartialStep = (g: DesignGroove) => g.preset?.kind === "partialStep";

/** Every partial step on the list, by its preset group (one groove each today). */
export function partialStepGroups(list: readonly DesignGroove[]): PartialStepGroup[] {
  const out: PartialStepGroup[] = [];
  for (const g of list) {
    if (g.preset?.kind !== "partialStep") continue;
    const found = out.find((s) => s.group === g.preset!.group);
    if (found) found.ids.push(g.id);
    else out.push({ group: g.preset.group, params: { ...(g.preset.params as PartialStepParams) }, ids: [g.id] });
  }
  return out;
}

/** A group name no groove on the list uses ("step", "step2", …). */
export function newStepGroup(list: readonly DesignGroove[]): string {
  const taken = (name: string) => list.some((g) => g.id === name || g.id.startsWith(`${name}-`) || g.preset?.group === name);
  if (!taken("step")) return "step";
  for (let n = 2; ; n++) if (!taken(`step${n}`)) return `step${n}`;
}

/** Append a partial step reaching across THIS band (its `spanMM` is the band's width). */
export function addPartialStep(list: readonly DesignGroove[], widthMM: number): DesignGroove[] {
  return [...list, ...partialStepGrooves({ ...PARTIAL_STEP_DEFAULTS, spanMM: widthMM }, newStepGroup(list))];
}

/** Rebuild one step from new params, in place (the list order — and so every other groove — is kept). */
export function regeneratePartialStep(list: readonly DesignGroove[], group: string, params: PartialStepParams): DesignGroove[] {
  const fresh = partialStepGrooves(params, group);
  const out: DesignGroove[] = [];
  let placed = false;
  for (const g of list) {
    if (isPartialStep(g) && g.preset!.group === group) {
      if (!placed) { out.push(...fresh); placed = true; }
      continue;
    }
    out.push(g);
  }
  if (!placed) out.push(...fresh);
  return out;
}

export function removePartialStep(list: readonly DesignGroove[], group: string): DesignGroove[] {
  return list.filter((g) => !(isPartialStep(g) && g.preset!.group === group));
}

export interface PartialStepSlider {
  key: Exclude<keyof PartialStepParams, "side" | "spanMM">;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
  hint?: string;
}

/** The step's sliders at `partialStepGrooves`'s own clamp (position from the band's width). */
export function partialStepSliders(widthMM: number): PartialStepSlider[] {
  const h = Math.max(0.5, Math.min(12, widthMM)) / 2;
  const lim = Math.max(0, h - 0.2);
  return [
    { key: "positionMM", label: "Step Line", min: -round(lim, 0.05), max: round(lim, 0.05), step: 0.05, unit: "mm", hint: "Where the step runs, from the band's centre line." },
    { key: "depthMM", label: "Depth", min: 0.1, max: 1, step: 0.05, unit: "mm" },
    { key: "centerArcMM", label: "Centre", min: -60, max: 60, step: 0.5, unit: "mm", hint: "Measured around the band from 12 o'clock." },
    { key: "lengthMM", label: "Length", min: 1, max: 60, step: 0.5, unit: "mm" },
    { key: "exitDeg", label: "Run-out Angle", min: 20, max: 90, step: 1, unit: "°", hint: "90° leaves the band straight out; smaller is a long slanting run-out." },
    { key: "cornerRadiusMM", label: "Corner", min: 0, max: 5, step: 0.1, unit: "mm" },
  ];
}

// ─── The true-V groove angle ─────────────────────────────────────────────────

/** The contract's V range (`DesignGroove.vAngleDeg`). */
export const V_ANGLE = { min: 30, max: 150, step: 1, start: 90 } as const;

/** Bead joints sweep metal and path grooves are edited elsewhere: neither takes a V angle. */
export const takesVAngle = (g: DesignGroove) =>
  g.direction !== "path" && g.jointType !== "milgrain" && g.jointType !== "milgrain2";

/**
 * The list with one groove's V angle set, or REMOVED (`null`) — the key is
 * deleted, not sent as `undefined`, so the groove goes back to its joint's own
 * section exactly. Turning a V off keeps the depth the angle derived.
 */
export function withVAngle(list: readonly DesignGroove[], id: string, deg: number | null): DesignGroove[] {
  return list.map((g) => {
    if (g.id !== id) return g;
    if (deg === null) {
      const { vAngleDeg: _drop, ...rest } = g;
      return rest;
    }
    return { ...g, vAngleDeg: Math.min(V_ANGLE.max, Math.max(V_ANGLE.min, deg)), jointType: "v60" as const };
  });
}

/** The depth a V of this angle cuts at this width (the engine's own derivation, for the read-out). */
export const vDepthMM = (widthMM: number, deg: number) => widthMM / 2 / Math.tan((deg * Math.PI) / 360);
